import { useEffect, useMemo, useState, useCallback } from "react";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getAllGrupos } from "../../../services/grupo.service";
import { getAllIdiomas } from "../../../services/idioma.service";
import { GrupoList } from "../../../modules/grupo/GrupoList";

export const GruposActivosPage = () => {
  const [idiomas, setIdiomas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [filtroIdioma, setFiltroIdioma] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- helpers para horarios (filtra soft delete / inhabilitados) ----
  const limpiarHorarios = useCallback((arr = []) => {
    const norm = (h) => ({
      id: h.idHorarioGrupo ?? h.idHorario ?? `${h?.dia?.nombre}-${h?.horaInicio}-${h?.horaFin}`,
      horaInicio: h.horaInicio ?? h.horario?.horaInicio ?? "?",
      horaFin: h.horaFin ?? h.horario?.horaFin ?? "?",
      dia: h.dia ?? h.horario?.dia ?? { nombre: "?" },
      eliminado: h.eliminado,
      activo: h.activo,
      estado: h.estado,
      fechaBaja: h.fechaBaja,
      habilitado: h.habilitado,
      inhabilitado: h.inhabilitado,
    });

    const visible = (h) => {
      if (h.eliminado) return false;
      if (h.fechaBaja) return false;
      if (h.inhabilitado) return false;
      if (h.habilitado === false) return false;
      if (h.activo === false) return false;
      if (h.estado != null) {
        const e = String(h.estado).toUpperCase();
        if (["INHABILITADO", "ELIMINADO", "DESHABILITADO", "0", "FALSE", "F"].includes(e)) return false;
      }
      return true;
    };

    const únicos = new Map();
    for (const raw of arr) {
      const h = norm(raw);
      if (visible(h)) únicos.set(h.id, h);
    }
    return Array.from(únicos.values());
  }, []);

  // Normalizar texto de búsqueda
  const normalizeText = useCallback((text = "") => {
    return text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }, []);

  // Función para agregar días hábiles (excluyendo sábados y domingos)
  const agregarDiasHabiles = useCallback((fecha, diasHabiles) => {
    const fechaResultado = new Date(fecha);
    fechaResultado.setHours(0, 0, 0, 0);
    
    let diasAgregados = 0;
    while (diasAgregados < diasHabiles) {
      fechaResultado.setDate(fechaResultado.getDate() + 1);
      const diaSemana = fechaResultado.getDay();
      // 0 = domingo, 6 = sábado
      if (diaSemana !== 0 && diaSemana !== 6) {
        diasAgregados++;
      }
    }
    
    return fechaResultado;
  }, []);

  // Verificar si un grupo está activo (fecha fin + 15 días hábiles aún no ha pasado)
  const esGrupoActivo = useCallback((grupo) => {
    // Si no tiene fecha fin, se considera activo
    if (!grupo?.fechaFin) return true;
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaFin = new Date(grupo.fechaFin);
    fechaFin.setHours(0, 0, 0, 0);
    
    // Calcular fecha fin + 15 días hábiles
    const fechaLimite = agregarDiasHabiles(fechaFin, 15);
    
    // El grupo está activo si hoy es menor o igual a la fecha límite
    return hoy <= fechaLimite;
  }, [agregarDiasHabiles]);

  // Cargar datos con manejo de errores + limpieza de horarios
  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [gruposRes, idiomasRes] = await Promise.all([getAllGrupos(), getAllIdiomas()]);

      // Aplico limpieza a los horarios de cada grupo
      const gruposLimpios = (gruposRes || []).map((g) => ({
        ...g,
        horarios: limpiarHorarios(g.horarios || []),
      }));

      setGrupos(gruposLimpios);
      setIdiomas(idiomasRes);
      setError(null);
    } catch (err) {
      console.error("Error cargando datos:", err);
      setError("Error al cargar los grupos. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  }, [limpiarHorarios]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Re-fetch al volver foco a la pestaña (útil al cerrar edición y volver)
  useEffect(() => {
    const onFocus = () => cargarDatos();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [cargarDatos]);

  // Procesar grupos para búsqueda y filtrar solo grupos activos
  const gruposProcesados = useMemo(() => {
    return grupos
      .filter((grupo) => esGrupoActivo(grupo))
      .map((grupo) => ({
        ...grupo,
        idIdioma: grupo.modulo?.idioma?.idIdioma || grupo.modulo?.idIdioma,
        searchString: `${grupo.nombre} ${grupo.nombreAntGrupo || ""} ${grupo.modulo?.nombre || ""} ${grupo.modulo?.sigla || ""}`,
      }));
  }, [grupos, esGrupoActivo]);

  // Filtrar grupos según criterios
  const gruposFiltrados = useMemo(() => {
    if (!filtroIdioma && !busqueda) return gruposProcesados;

    const terminoBusqueda = busqueda ? normalizeText(busqueda) : null;

    return gruposProcesados.filter((grupo) => {
      if (filtroIdioma && grupo.idIdioma !== filtroIdioma) return false;
      if (terminoBusqueda) return normalizeText(grupo.searchString).includes(terminoBusqueda);
      return true;
    });
  }, [gruposProcesados, filtroIdioma, busqueda, normalizeText]);

  if (loading) {
    return (
      <ContenedorPageTemplate titulo="Grupos Activos">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-lg">Cargando grupos activos...</span>
        </div>
      </ContenedorPageTemplate>
    );
  }

  if (error) {
    return (
      <ContenedorPageTemplate titulo="Grupos Activos">
        <div className="flex flex-col items-center justify-center h-64 p-4 bg-red-50 rounded-lg">
          <p className="text-red-600 font-medium text-lg mb-3">{error}</p>
          <button
            onClick={cargarDatos}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </ContenedorPageTemplate>
    );
  }

  return (
    <ContenedorPageTemplate titulo="Grupos Activos">
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
        <div className="relative w-full sm:w-auto">
          <select
            value={filtroIdioma}
            onChange={(e) => setFiltroIdioma(e.target.value)}
            className="w-full appearance-none bg-blue-600 text-white font-medium py-2 px-4 pr-8 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
          >
            <option value="" className="bg-white text-gray-800">
              Todos los idiomas
            </option>
            {idiomas.map(({ idIdioma, nombre }) => (
              <option key={idIdioma} value={idIdioma} className="bg-white text-gray-800">
                {nombre}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <input
          type="text"
          placeholder="Buscar por nombre, módulo, sigla…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="w-full flex-1 border border-gray-300 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
        />
      </div>

      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <p className="font-medium text-lg">
          Total de grupos activos: <span className="font-bold text-blue-700">{gruposFiltrados.length}</span>
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {gruposFiltrados.length > 0
            ? "Mostrando solo grupos activos"
            : "No se encontraron grupos activos con los filtros seleccionados"}
        </p>
      </div>

      <GrupoList grupos={gruposFiltrados} />
    </ContenedorPageTemplate>
  );
};